import React, { useCallback, useState } from "react";

import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faPaperPlane } from "@fortawesome/pro-solid-svg-icons/faPaperPlane";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip, { TooltipProps } from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import EmailSchedule from "@mapmycustomers/shared/types/email/EmailSchedule";
import { stopEventPropagation } from "@mapmycustomers/shared/util/browser";
import { Button, ButtonProps } from "@mapmycustomers/ui";

import EmailScheduleSendPopover from "../EmailScheduleSendPopover";

import styles from "./EmailSendAndCreateActivitiesButton.module.scss";

const messages = defineMessages({
  label: {
    id: "email.creation.sendAndCreateButton.label",
    defaultMessage: "Send and Create",
    description: "Send and Create button in email creation modal",
  },
  scheduleLabel: {
    id: "email.creation.sendAndCreateButton.scheduleLabel",
    defaultMessage: "Schedule",
    description: "Schedule button label when a schedule time is set",
  },
  scheduleSend: {
    id: "email.creation.sendAndCreateButton.scheduleSend",
    defaultMessage: "Schedule send",
    description: "Tooltip for Schedule Send button in email creation modal",
  },
  scheduleTooltip: {
    id: "email.creation.sendAndCreateButton.schedule.tooltip",
    defaultMessage: "Schedule {count, plural, one {email} other {emails}}",
    description: "Tooltip for Send and Create button when a schedule time is set",
  },
  tooltip: {
    id: "email.creation.sendAndCreateButton.tooltip",
    defaultMessage:
      "Send {count, plural, one {email} other {emails}} and create {count, plural, one {activity} other {activities}}",
    description: "Tooltip for Send and Create button in email creation modal",
  },
});

interface SendAndCreateEmailButtonProps extends ButtonProps {
  onScheduleChange?: (schedule: EmailSchedule | undefined) => void;
  recipientCount: number;
  schedule?: EmailSchedule;
  tooltipTitle?: TooltipProps["title"];
}

const EmailSendAndCreateActivitiesButton = ({
  disabled,
  loading,
  onClick,
  onScheduleChange,
  recipientCount,
  schedule,
  tooltipTitle,
  ...props
}: SendAndCreateEmailButtonProps) => {
  const intl = useIntl();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleScheduleChange = useCallback(
    (newSchedule: EmailSchedule) => {
      onScheduleChange?.(newSchedule);
    },
    [onScheduleChange]
  );

  const defaultTooltip = intl.formatMessage(
    schedule ? messages.scheduleTooltip : messages.tooltip,
    {
      count: recipientCount,
    }
  );
  const buttonLabel = intl.formatMessage(schedule ? messages.scheduleLabel : messages.label);

  const sendButton = (
    <Tooltip title={tooltipTitle ?? defaultTooltip}>
      <span>
        <Button
          {...props}
          disabled={disabled}
          leftIcon={schedule ? undefined : <FontAwesomeIcon icon={faPaperPlane} />}
          loading={loading}
          onClick={onClick}
          type="primary"
        >
          {buttonLabel}
        </Button>
      </span>
    </Tooltip>
  );

  if (!onScheduleChange) {
    return sendButton;
  }

  return (
    <span className={styles.container}>
      <div onClick={stopEventPropagation}>
        <EmailScheduleSendPopover
          disabled={disabled}
          onChange={handleScheduleChange}
          onOpenChange={setPopoverOpen}
          value={schedule}
        >
          <Tooltip title={popoverOpen ? null : intl.formatMessage(messages.scheduleSend)}>
            <Button
              disabled={disabled}
              icon={<FontAwesomeIcon icon={faClock} />}
              iconOnly
              type="default"
            />
          </Tooltip>
        </EmailScheduleSendPopover>
      </div>
      {sendButton}
    </span>
  );
};

export default EmailSendAndCreateActivitiesButton;
